/**
 * @file SelectField.tsx
 * @description Однострочный селект по плоскому списку опций (phase19.23 A3).
 *
 * Нативный `<select>` в админке запрещён: ОС рисует его по-своему, тач-цель
 * меньше 48px и подписи не совпадают с DS. Обёртка над `Select` закрывает
 * типовой случай «значение + список опций» и сама собирает карту подписей для
 * триггера (без неё base-ui показывает сырое value).
 *
 * `size="wms"` — триггер 56px для складских экранов (правила warehouse-UI).
 * Пункты списка — тач-цель ≥ 48px при любом размере (phase19.26 шаг 7.1, Р6):
 * раньше 48px было только у `wms`, а в остальных списках пункт был ~32px.
 *
 * ```tsx
 * <SelectField
 *   value={warehouseId}
 *   onChange={setWarehouseId}
 *   options={warehouses.map((w) => ({ value: w.id, label: w.name }))}
 *   placeholder="Все склады"
 *   ariaLabel="Склад"
 *   size="wms"
 * />
 * ```
 */

import { useMemo } from "react";

import { cn } from "@/lib/utils";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./select";

/** Опция селекта. Пустое `value` — «все/не выбрано», показывает placeholder. */
export interface SelectFieldOption {
  value: string;
  label: string;
  disabled?: boolean;
}

interface Props {
  value: string;
  onChange: (value: string) => void;
  options: SelectFieldOption[];
  /** Текст при пустом значении: «Все склады», «— склад —». */
  placeholder?: string;
  ariaLabel: string;
  /** `wms` — 56px (склад), `default` — 48px. */
  size?: "default" | "wms";
  disabled?: boolean;
  id?: string;
  className?: string;
}

export function SelectField({
  value,
  onChange,
  options,
  placeholder = "Выбрать…",
  ariaLabel,
  size = "default",
  disabled,
  id,
  className,
}: Props) {
  const labels = useMemo(
    () => Object.fromEntries(options.map((o) => [o.value, o.label])),
    [options],
  );

  return (
    <Select
      value={value}
      disabled={disabled}
      onValueChange={(next: unknown) => onChange(next == null ? "" : String(next))}
    >
      <SelectTrigger
        id={id}
        aria-label={ariaLabel}
        className={cn(
          "rounded-xl border border-on-surface/15 bg-surface-lowest text-base",
          size === "wms" ? "h-auto min-h-[56px]" : "h-auto min-h-12",
          className,
        )}
      >
        {/*
          Пустое значение — это тоже вариант («Все склады»), а не «ничего не
          выбрано»: если опция с `value: ""` есть в списке, показываем её
          подпись, иначе падаем на placeholder.
        */}
        <SelectValue placeholder={placeholder}>
          {(raw: unknown) => {
            const key = raw == null ? "" : String(raw);
            return labels[key] ?? (key === "" ? placeholder : key);
          }}
        </SelectValue>
      </SelectTrigger>
      <SelectContent>
        {options.map((o) => (
          <SelectItem
            key={o.value}
            value={o.value}
            disabled={o.disabled}
            className="min-h-12 py-2.5"
          >
            {o.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}
